import React, { useRef, useState } from "react";
import HKPConfirmDialog from "../../common/Components/HKPConfirmDialog";
import { completeIdentityMutation, identityCode, identityError, identityMutationKey, identityRequest, ScanStage } from "../../services/physicalIdentityApi";
import { useKT } from "../karkhana/i18n/kt";

/** Button text fits in 28 characters: a long stage name is cut at 24; the confirm sheet shows it whole. */
export const shortStage = (label: string) => (label.length > 24 ? `${label.slice(0, 23).trimEnd()}…` : label);

type Props = {
  token: string;
  stage: ScanStage;
  /** "Saree 214" or the product name, for the confirm sheet. */
  pieceName: string;
  /** Reload the stage from the server: after a move, and after someone else moved it first (409). */
  onMoved: () => void;
};

/**
 * The one way to move a scanned piece on, for the owner's strip on View Product and the supervisor's card alike:
 * one filled button naming the next stage, one confirm sheet, then the server's answer. It sends the stage the
 * viewer saw, so if someone moved the piece meanwhile the server refuses and the real stage loads.
 */
export default function StageMoveButton({ token, stage, pieceName, onMoved }: Props) {
  const { t } = useKT();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: "status" | "alert"; text: string }>();
  const lock = useRef(false);
  if (!stage.next || !stage.nextLabel) return null;
  const label = stage.nextLabel;

  const move = async () => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setMessage(undefined);
    const path = `/${token}/journey`;
    const body = { kind: "STAGE", value: stage.next, loomCode: null, expectedStage: stage.current || "NONE" };
    try {
      await identityRequest(path, body, identityMutationKey(path, body));
      completeIdentityMutation();
      setAsking(false);
      setMessage({ kind: "status", text: t("Moved to {{stage}}.", { stage: label }) });
      onMoved();
    } catch (e) {
      setAsking(false);
      if (identityCode(e) === "STAGE_CHANGED") { completeIdentityMutation(); onMoved(); }
      setMessage({ kind: "alert", text: t(identityError(e)) });
    } finally {
      lock.current = false; setBusy(false);
    }
  };

  return <div className="scan-move">
    <button type="button" className="scan-btn is-primary is-move" disabled={busy} onClick={() => { setMessage(undefined); setAsking(true); }}>
      {t("Move to {{stage}}", { stage: shortStage(label) })}
    </button>
    {message && <p role={message.kind} className={`scan-move__note${message.kind === "alert" ? " is-alert" : ""}`}>{message.text}</p>}
    <HKPConfirmDialog open={asking} title={t("Move to {{stage}}", { stage: shortStage(label) })}
      description={t("Move {{piece}} to {{stage}} today?", { piece: pieceName, stage: label })}
      confirmLabel={t("Move")} busyLabel={t("Moving…")} busy={busy} tone="primary"
      onCancel={() => setAsking(false)} onConfirm={() => void move()} />
  </div>;
}
